import React, { useState } from "react";
import { ChevronDown, Pencil, Trash2, Copy, CheckCircle2, Circle, ChevronUp, ChevronDown as ChevronDownIcon } from "lucide-react";
import { StatusBadge, Select, IconBtn } from "./ui-kit";
import TaskDescription from "./TaskDescription";
import { formatCostDisplay, TASK_STATUSES } from "@/lib/format";

const TaskItem = ({ task, onEdit, onDelete, onDuplicate, onStatusChange, onToggleChosen, onMoveUp, onMoveDown, canMoveUp = false, canMoveDown = false, showDuplicate = false, showChosen = false, showReorder = false, testIdPrefix = "task" }) => {
  const [open, setOpen] = useState(false);
  const chosen = !!task.client_chosen;

  return (
    <div
      data-testid={`${testIdPrefix}-item-${task.id}`}
      data-chosen={chosen ? "true" : "false"}
      className={`bg-white border rounded-xl overflow-hidden animate-fade-in transition-all ${
        chosen
          ? "border-emerald-400 ring-2 ring-emerald-200/60 shadow-sm"
          : "border-zinc-200"
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-zinc-400 hover:text-zinc-900 transition-transform"
          data-testid={`${testIdPrefix}-toggle-${task.id}`}
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-zinc-950">{task.name}</p>
            {task.task_type ? (
              <span
                data-testid={`${testIdPrefix}-type-${task.id}`}
                className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${
                  task.task_type === "Design"
                    ? "bg-violet-50 text-violet-700 border-violet-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
              >
                {task.task_type}
              </span>
            ) : null}
            {chosen && (
              <span
                data-testid={`${testIdPrefix}-chosen-badge-${task.id}`}
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" />
                Client Chosen
              </span>
            )}
          </div>
          {!open && task.description && (
            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{task.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showChosen && onToggleChosen && (
            <button
              type="button"
              onClick={() => onToggleChosen(task.id)}
              data-testid={`${testIdPrefix}-chosen-toggle-${task.id}`}
              title={chosen ? "Unmark as client chosen" : "Mark as client chosen option"}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                chosen
                  ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                  : "bg-white text-zinc-500 border-zinc-200 hover:border-emerald-400 hover:text-emerald-700"
              }`}
            >
              {chosen ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{chosen ? "Chosen" : "Mark chosen"}</span>
            </button>
          )}
          {onStatusChange ? (
            <Select
              value={task.status}
              onChange={(e) => onStatusChange(task.id, e.target.value)}
              className="!py-1 !px-2 !w-auto text-xs"
              data-testid={`${testIdPrefix}-status-${task.id}`}
            >
              {TASK_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          ) : (
            <StatusBadge status={task.status} />
          )}
          {showDuplicate && (
            <IconBtn onClick={() => onDuplicate(task.id)} title="Duplicate task" data-testid={`${testIdPrefix}-duplicate-${task.id}`}>
              <Copy className="w-4 h-4" />
            </IconBtn>
          )}
          {showReorder && (
            <div className="inline-flex flex-col border border-zinc-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => onMoveUp && onMoveUp(task.id)}
                disabled={!canMoveUp}
                title="Move up"
                data-testid={`${testIdPrefix}-move-up-${task.id}`}
                className="px-1.5 py-0.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <div className="h-px bg-zinc-200" />
              <button
                type="button"
                onClick={() => onMoveDown && onMoveDown(task.id)}
                disabled={!canMoveDown}
                title="Move down"
                data-testid={`${testIdPrefix}-move-down-${task.id}`}
                className="px-1.5 py-0.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronDownIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <IconBtn onClick={() => onEdit(task)} title="Edit task" data-testid={`${testIdPrefix}-edit-${task.id}`}>
            <Pencil className="w-4 h-4" />
          </IconBtn>
          <IconBtn variant="destructive" onClick={() => onDelete(task.id)} title="Delete task" data-testid={`${testIdPrefix}-delete-${task.id}`}>
            <Trash2 className="w-4 h-4" />
          </IconBtn>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pl-11 text-sm text-zinc-700 border-t border-zinc-100 pt-3 space-y-1.5 animate-fade-in">
          {task.description && (
            <div>
              <p className="font-semibold text-zinc-900 mb-1">Description:</p>
              <TaskDescription text={task.description} className="text-zinc-700" bulletClassName="text-zinc-700" />
            </div>
          )}
          {task.notes && (
            <p className="text-zinc-500 italic">Note: {task.notes}</p>
          )}
          <p className="font-semibold text-zinc-950 pt-1">{formatCostDisplay(task)}</p>
        </div>
      )}
    </div>
  );
};

export default TaskItem;
